import {
  Edit,
  ReferenceArrayInput,
  ReferenceInput,
  SimpleForm,
  SelectInput,
  SelectArrayInput,
  SaveButton,
  DeleteButton,
  Toolbar,
  useRedirect,
  FunctionField,
  TimeInput,
  useNotify,
  useDataProvider,
  useRefresh,
  useRecordContext,
} from 'react-admin';
import { Card, CardContent, Box, Typography, Button, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { convertToISO, daysOfWeek, generateCronExpression, parseCronExpression } from '../../utils';
import { CustomActions } from '../../components';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useCognitoUser } from '../../hooks';

const CustomToolbar = ({isEditable}) => {
  const record = useRecordContext();
  const redirect = useRedirect(); // Hook to redirect to another page
  const theme = useTheme(); // Access the theme
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const backgroundColor = theme.palette.background.paper; // Default background color
  const isDeletable = record.records.items.length === 0;

  const handleCancel = () => {
    redirect('list', 'schedules');
  };

  const handleSuccessUpdate = async (res) => {
    try {
      const { id, operator_ids, route_id } = res;
      const data = {
        id,
        operator_ids,
        cron_expression: generateCronExpression(res.day_names, res.time_name),
        route_id,
      };

      await dataProvider.update("schedules", { data });
      notify('Schedule updated successfully', { type: 'success' });
      refresh();
      redirect('list', 'schedules');
    } catch (error) {
      console.error('Error updating schedule:', error);
      notify('Error updating schedule', { type: 'error' });
    }
  };

  const handleSuccessDelete = async (res) => {
    try {
      const { id } = res;

      await dataProvider.update("schedules", { data: { id, deleted: true } });
      notify('Schedule deleted successfully', { type: 'success' });
      redirect('list', 'schedules');
    } catch (error) {
      console.error('Error deleting schedule:', error);
      notify('Error deleting schedule', { type: 'error' });
    }
  };

  return (
    <Toolbar
      sx={{
        backgroundColor, // Set the background color
      }}
    >
      <Box display="flex" justifyContent="space-between" width="100%">
        <Box display='flex' alignItems='center'>
          <DeleteButton
            type='button'
            size='small'
            label='Delete'
            sx={{textTransform: 'none', minWidth: '95px', minHeight: '30.75px'}}
            mutationOptions={{ onSuccess: handleSuccessDelete }}
            disabled={!isDeletable || !isEditable}
          />
          {!isDeletable && (
            <Tooltip
              title='This schedule is referenced by other logs.'
              placement='top'
            >
              <InfoOutlinedIcon sx={{ fontSize: 15 }} />
            </Tooltip>
          )}
        </Box>
        <Box>
          <Button
            variant="text"
            onClick={handleCancel}
            sx={{
              color: theme.palette.common.white, // Optional: primary color for cancel button text
              borderColor: theme.palette.primary.main, // Optional: primary color for cancel button border
              marginRight: theme.spacing(1),
              textTransform: 'none',
              minWidth: '95px',
            }}
            size='small'
          >
            Cancel
          </Button>
          <SaveButton
            type='button'
            label='Update'
            size='small'
            icon={null}
            sx={{textTransform: 'none', minWidth: '95px'}}
            mutationOptions={{ onSuccess: handleSuccessUpdate }}
            disabled={!isEditable}
          />
        </Box>
      </Box>
    </Toolbar>
  );
};

const EditSchedule = () => {
  const currentUser = useCognitoUser();
  const isEditable = currentUser.group !== "customer";

  return (
    <Card variant='elevation' sx={{ width: '50%' }}>
      <CardContent>
        <Edit actions={<CustomActions title="Edit Schedule" type='edit' />}>
          <SimpleForm toolbar={<CustomToolbar isEditable={isEditable} />} disabled={!isEditable} >
            <Typography variant="subtitle2">
              Details
            </Typography>
            <FunctionField
              sx={{ width: '100%' }}
              render={(record) => (
                <SelectArrayInput
                  source="day_names"
                  label="Select Day"
                  choices={daysOfWeek}
                  optionValue='id'
                  optionText='name'
                  defaultValue={parseCronExpression(record.cron_expression)?.days || []}
                  fullWidth
                />
              )}
            />
            <FunctionField
              sx={{ width: '100%' }}
              render={(record) => (
                <TimeInput
                  source="time_name"
                  label="Time"
                  defaultValue={convertToISO(parseCronExpression(record.cron_expression)?.time || '-')}
                  fullWidth
                />
              )}
            />
            <ReferenceInput source="route_id" reference="routes" label="Route">
              <SelectInput optionText="name" fullWidth />
            </ReferenceInput>
            <Typography variant="subtitle2">
              Assign To
            </Typography>
            <ReferenceArrayInput source="operator_ids" reference="cognitoUsers" label="Operators">
              <SelectArrayInput optionText="name" fullWidth style={{width: '100%'}}/>
            </ReferenceArrayInput>
          </SimpleForm>
        </Edit>
      </CardContent>
    </Card>
  )
};

export default EditSchedule;
export { EditSchedule };